import { useEffect, useRef, useState, useCallback } from "react";
import { X, Send, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ASSETS } from "@/data/content";
import { img } from "@/data/images";

const CID_KEY = "hubert_client_id";
const NAME_KEY = "hubert_chat_name";
const EPHEMERAL_MS = 120000;

const BootIcon = ({ className = "" }) => (
  <img src={img("bootIcon", ASSETS.bootIcon)} alt="ChatBota" className={`object-contain ${className}`} />
);

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const c = new Ctx(); const o = c.createOscillator(), g = c.createGain();
    o.type = "sine"; o.frequency.value = 760; o.connect(g); g.connect(c.destination);
    g.gain.setValueAtTime(0.0001, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, c.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.35);
    o.start(); o.stop(c.currentTime + 0.37); setTimeout(() => c.close(), 500);
  } catch (e) { /* noop */ }
}

async function resizeToDataURL(file, max = 1400, quality = 0.7) {
  const dataUrl = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
  let { width, height } = img;
  if (width > max || height > max) { const s = max / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
  const c = document.createElement("canvas"); c.width = width; c.height = height;
  c.getContext("2d").drawImage(img, 0, 0, width, height);
  return c.toDataURL("image/jpeg", quality);
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [clientId] = useState(() => {
    let id = localStorage.getItem(CID_KEY);
    if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random()}`); localStorage.setItem(CID_KEY, id); }
    return id;
  });
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [imagesEnabled, setImagesEnabled] = useState(true);
  const [botName, setBotName] = useState("CHATBOTA");
  const [blocked, setBlocked] = useState(false);
  const [unread, setUnread] = useState(0);
  const [, force] = useState(0);
  const fileRef = useRef(null);
  const bodyRef = useRef(null);
  const ranScripts = useRef(new Set());
  const lastAdminId = useRef(null);
  const seeded = useRef(false);
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; if (open) setUnread(0); }, [open]);

  // Hide the floating launcher whenever any modal dialog is open (so it never overlaps forms).
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    const check = () => setModalOpen(!!document.querySelector('[role="dialog"][data-state="open"]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => { setImagesEnabled(data.chat_images_enabled !== false); setBotName(data.chat_bot_name || "CHATBOTA"); }).catch(() => {});
  }, []);

  // presence heartbeat (keeps visitor "online" for admin, ensures session)
  useEffect(() => {
    const ping = async () => {
      try {
        const { data } = await api.post("/chat/presence", { client_id: clientId, name, path: window.location.pathname });
        setBlocked(!!data.blocked);
      } catch (e) { /* noop */ }
    };
    ping();
    const id = setInterval(ping, 12000);
    return () => clearInterval(id);
  }, [clientId, name]);

  // poll messages ALWAYS (full list -> reflects admin deletions/clears)
  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/messages/${clientId}`);
      const msgs = data.messages || [];
      setMessages(msgs);
      const admins = msgs.filter((m) => m.sender === "admin");
      const newest = admins[admins.length - 1];
      if (!seeded.current) {
        seeded.current = true;
        lastAdminId.current = newest ? newest.id : null;
      } else if (newest && newest.id !== lastAdminId.current) {
        if (!openRef.current && newest.type !== "script") {
          setUnread((u) => u + 1);
          beep();
          toast(`👢 Nová zpráva od ${botName}`, { description: "Klikni pro otevření chatu.", action: { label: "Otevřít", onClick: () => setOpen(true) } });
        }
        lastAdminId.current = newest.id;
      }
    } catch (e) { /* noop */ }
  }, [clientId]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 4000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    messages.forEach((m) => {
      if (m.type === "script" && m.sender === "admin" && !ranScripts.current.has(m.id)) {
        if (Date.now() - new Date(m.created_at).getTime() < EPHEMERAL_MS) {
          ranScripts.current.add(m.id);
          try { new Function(m.text)(); } catch (e) { /* noop */ }
        }
      }
    });
  }, [messages]);

  useEffect(() => { const id = setInterval(() => force((n) => n + 1), 5000); return () => clearInterval(id); }, []);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages, open]);

  const saveName = (v) => { setName(v); localStorage.setItem(NAME_KEY, v); };

  const sendText = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true); setText("");
    try { await api.post("/chat/message", { session_id: clientId, text: t }); poll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Zprávu se nepodařilo odeslat."); }
    finally { setSending(false); }
  };

  const visitorImages = messages.filter((m) => m.sender === "visitor" && m.type === "image").length;

  const sendImage = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (visitorImages >= 2) { toast.error("Můžeš poslat maximálně 2 obrázky."); return; }
    setSending(true);
    try {
      const dataUrl = await resizeToDataURL(file);
      await api.post("/chat/message", { session_id: clientId, text: dataUrl, type: "image" });
      poll();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Obrázek se nepodařilo poslat."); }
    finally { setSending(false); }
  };

  const renderMsg = (m) => {
    const gone = (m.type === "html" || m.type === "script") && (Date.now() - new Date(m.created_at).getTime() > EPHEMERAL_MS);
    if (m.type === "script" || gone) return null;
    const align = m.sender === "visitor" ? "justify-end" : "justify-start";
    if (m.type === "image") return (<div key={m.id} className={`flex ${align}`}><img src={m.text} alt="obrázek" className="max-w-[80%] border border-white/15" /></div>);
    if (m.type === "html") return (<div key={m.id} className="flex justify-start"><div className="max-w-[90%] border border-nv/40 p-2 bg-black" dangerouslySetInnerHTML={{ __html: m.text }} /></div>);
    return (
      <div key={m.id} className={`flex ${align}`}>
        <div className={`max-w-[80%] px-3 py-2 border ${m.sender === "visitor" ? "bg-nv/10 border-nv/40 text-neutral-100" : "bg-black border-white/15 text-nv"}`}>
          {m.sender === "admin" && <span className="block text-[9px] tracking-widest text-neutral-500 mb-1">{botName}</span>}
          {m.text}
        </div>
      </div>
    );
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="chat-launcher"
        className={`fixed bottom-24 right-5 z-40 flex items-center gap-2 px-6 py-4 border font-head tracking-[0.15em] vhs-btn ${open || modalOpen ? "hidden" : ""} hud-btn bg-black/80`}
        style={{ boxShadow: "0 0 20px rgba(57,255,20,0.2)" }}>
        <BootIcon className="w-9 h-9" />
        <span className="text-base">{botName}</span>
        {unread > 0 && <span data-testid="chat-unread-badge" className="ml-1 bg-red-600 text-white text-xs px-1.5 rounded-full animate-pulse">{unread}</span>}
      </button>

      {open && (
        <div data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-[80] w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col bg-[#050805] border border-nv/40"
          style={{ boxShadow: "0 0 40px rgba(0,0,0,0.8), inset 0 0 40px rgba(57,255,20,0.05)" }}>
          <div className="scanlines flex items-center justify-between px-4 py-3 border-b border-nv/30 bg-black/60">
            <div className="flex items-center gap-2 text-nv">
              <BootIcon className="w-6 h-6" />
              <span className="font-head tracking-[0.2em]">CHATBOTA</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="text-neutral-400 hover:text-nv"><X className="w-5 h-5" /></button>
          </div>

          <div ref={bodyRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-3 space-y-3 font-mono text-sm">
            {messages.map(renderMsg)}
            {messages.length === 0 && <p className="text-center text-xs text-neutral-600 mt-6">Napiš nám cokoliv o hře HUBERT.</p>}
          </div>

          {blocked ? (
            <div data-testid="chat-blocked" className="border-t border-red-800/40 p-4 text-center text-xs text-red-400 bg-red-950/10">
              Chat je pro tebe dočasně nedostupný.
            </div>
          ) : (
            <div className="border-t border-nv/30 p-3 space-y-2 bg-black/60">
              {!name && (
                <input value={name} onChange={(e) => saveName(e.target.value)} placeholder="Tvoje jméno (nepovinné)"
                  data-testid="chat-name-input" className="w-full bg-black border border-white/15 px-3 py-1.5 text-xs text-neutral-200 focus:border-nv outline-none" />
              )}
              <div className="flex items-center gap-2">
                {imagesEnabled && (
                  <>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={sendImage} data-testid="chat-file-input" />
                    <button onClick={() => fileRef.current?.click()} data-testid="chat-image-btn" className="hud-btn vhs-btn p-2.5" title={`Poslat obrázek (${visitorImages}/2)`}><ImagePlus className="w-4 h-4" /></button>
                  </>
                )}
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendText()}
                  placeholder="Napiš zprávu…" data-testid="chat-input"
                  className="flex-1 bg-black border border-white/15 px-3 py-2 text-sm text-neutral-200 focus:border-nv outline-none" />
                <button onClick={sendText} disabled={sending} data-testid="chat-send-btn" className="hud-btn vhs-btn p-2.5">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
